import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { Edge, getOutgoers, Node, useEdges, useNodes, useReactFlow } from 'reactflow';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { Delete } from '@mui/icons-material';

type ParamsPanelProps = {
  nodeId: string;
  bindProps: (parent: Node, prop: string, add: boolean, data: any) => void;
};

export const ParamsPanel: FunctionComponent<ParamsPanelProps> = ({ nodeId, bindProps }: ParamsPanelProps) => {
  const nodes: Node[] = useNodes();
  const edges: Edge[] = useEdges();

  const isPart = useMemo(() => nodeId.includes('|'), [nodeId]);
  const [machineId, partId] = useMemo(() => nodeId.split('|'), [nodeId]);

  const parent: Node = useMemo(() => {
    if (isPart) {
      let node = nodes.find((node) => node.id === machineId) ?? nodes[0];
      return node;
    } else {
      let node = nodes.find((node) => node.id === nodeId);
      let _outgoers: Node[] = [];
      if (!!node) _outgoers = getOutgoers(node, nodes, edges);
      return _outgoers[0];
    }
  }, [isPart, machineId, nodeId, nodes, edges]);

  const node: Node | undefined = useMemo(() => {
    return nodes.find((node) => node.id === machineId ?? nodeId);
  }, [nodeId, nodes, edges]);

  const part: Node | undefined = useMemo(() => {
    return node?.data.parts?.find((p: any) => partId === p.id);
  }, [nodeId, node]);

  const nodeName = useMemo(() => {
    if (isPart) {
      return node?.data?.parts?.find((p: any) => p.id === partId)?.name;
    } else {
      return node?.data?.name;
    }
  }, [nodeId, node]);

  // let _outgoers = getOutgoers(node, nodes, edges);
  // if(node.data?.type === 'part' && _outgoers[0]) {
  //   _outgoers = getOutgoers(_outgoers[0], nodes, edges);
  // }
  // const controller = _outgoers[0];
  const handleChange = useCallback(
    (e: any) => {
      // let node = nodes.find(node => node.id === nodeId);
      if (!!node) {
        let data = { ...node.data };
        if (isPart) {
          data.parts = data.parts?.map((p: any) => {
            if (p.id === partId) {
              p.name = e.target.value ?? '';
            }
            return p;
          });
        } else {
          data.name = e.target.value ?? '';
        }
        bindProps(parent, '', false, data);
      }
    },
    [parent, nodeId]
  );

  const handleChecked = useCallback(
    (e: any, value: any, prop: any) => {
      // let node = nodes.find(node => node.id === nodeId);
      if (!!node) {
        let data = { ...node.data };
        if (!Array.isArray(data.props)) {
          data.props = [];
        }
        let _props: any[] = [];
        if (isPart) {
          _props = data.parts?.find((p: any) => p.id === partId)?.data?.props ?? [];
        }
        if (value) {
          delete prop.usedBy;
          if (isPart) {
            prop.machine = parent.id;
          } else if (node.data.type === 'machine') {
            prop.controller = parent.id;
          }
          if (isPart) {
            _props.push(prop);
            // data.props = data.props?.map((p: any) => {
            //   if(p.name === prop.name) {
            //     p.usedBy = partId;
            //   }
            //   return p;
            // })
          } else data.props.push(prop);
        } else {
          if (isPart) _props = _props.filter((p: any) => p.name !== prop.name);
          else data.props = data.props?.filter((p: any) => p.name !== prop.name);
        }
        if (isPart) {
          data.parts = data.parts?.map((p: any) => {
            if (p.id === partId) {
              p.data.props = _props;
            }
            return p;
          });
        }
        bindProps(parent, prop.name, value, data);
      }
    },
    [node, parent, isPart, partId]
  );
  // console.log(parent);
  if (!parent) {
    return <h6>Please connect a controller/machine in order to proceed</h6>;
  }
  return (
    <Grid container>
      <Stack spacing={1}>
        <TextField id="standard-basic" label="Name" variant="standard" value={nodeName ?? ''} onChange={handleChange} />
        {/*{node?.data?.parts?.map((part: any, index: number) => {*/}
        {/*  return <TextField id="standard-basic" label={"Part name " + (index + 1)} variant="standard"*/}
        {/*                    value={part.name ?? ''} onChange={(e: any) => {*/}
        {/*    handlePartName(part, e.target.value)*/}
        {/*  }}/>*/}
        {/*})}*/}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Parameter</TableCell>
              <TableCell>Used by</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(parent.data?.props ?? []).length === 0 && (
              <TableCell colSpan={3}>
                <h5>No props added to controller or selected by connected machine</h5>
              </TableCell>
            )}
            {(parent.data?.props ?? [])?.map((prop: any) => {
              const usedBy: any =
                node?.data?.parts?.find((part: any) => part.id === prop.usedBy) ??
                nodes.find((node) => node.id === prop.usedBy);
              return (
                <TableRow>
                  <TableCell>
                    <Checkbox
                      disabled={!!prop.usedBy && (isPart ? prop.usedBy !== partId : prop.usedBy !== nodeId)}
                      checked={isPart ? prop.usedBy === partId : prop.usedBy === nodeId}
                      onChange={(e, v) => handleChecked(e, v, { ...prop })}
                    />
                  </TableCell>
                  <TableCell>{prop.display_name}</TableCell>
                  <TableCell>{usedBy?.name ?? usedBy?.data.name ?? usedBy?.data.label ?? 'Unused'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Stack>
    </Grid>
  );
};
